import { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Package, 
  FileText,
  AlertTriangle,
  Download,
  Loader2,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  getDashboardStats, 
  getUtilizationStats, 
  getAssets,
  getDamageReports 
} from '../lib/api';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#FF5F00', '#1A202C', '#F6E05E', '#10B981', '#3B82F6', '#8B5CF6'];

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [utilization, setUtilization] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [damageReports, setDamageReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, utilRes, assetsRes, damageRes] = await Promise.all([
          getDashboardStats(),
          getUtilizationStats(),
          getAssets(),
          getDamageReports()
        ]);
        
        setStats(statsRes.data);
        setUtilization(utilRes.data);
        setDamageReports(damageRes.data);

        // Calculate status distribution
        const assets = assetsRes.data;
        const statusCounts = assets.reduce((acc, asset) => {
          acc[asset.status] = (acc[asset.status] || 0) + 1;
          return acc;
        }, {});
        
        setStatusData(Object.entries(statusCounts).map(([status, count]) => ({
          name: status.replace('_', ' '),
          value: count
        })));
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/reports/assets/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asset-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to download PDF report');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/reports/assets/excel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asset-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel report downloaded');
    } catch (error) {
      toast.error('Failed to download Excel report');
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
            Reports
          </h1>
          <p className="font-body text-slate-500 mt-1">
            Analytics and insights
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={downloadPDF}
            disabled={downloadingPDF}
            variant="outline"
            className="border-2 border-slate-200 font-heading font-bold uppercase tracking-wide shadow-industrial"
            data-testid="download-pdf-btn"
          >
            {downloadingPDF ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
          <Button 
            onClick={downloadExcel}
            disabled={downloadingExcel}
            className="bg-green-600 hover:bg-green-700 font-heading font-bold uppercase tracking-wide shadow-industrial"
            data-testid="download-excel-btn"
          >
            {downloadingExcel ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Download Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-heading font-black text-2xl">{stats?.total_assets || 0}</p>
                <p className="font-mono text-xs text-slate-400 uppercase">Total Assets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-heading font-black text-2xl">{stats?.available || 0}</p>
                <p className="font-mono text-xs text-slate-400 uppercase">Available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-heading font-black text-2xl">{stats?.on_hire || 0}</p>
                <p className="font-mono text-xs text-slate-400 uppercase">On Hire</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-heading font-black text-2xl">{damageReports.length}</p>
                <p className="font-mono text-xs text-slate-400 uppercase">Damage Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Assets by Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {utilization.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={utilization}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="category" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#FF5F00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Assets by Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Damage Reports */}
      <Card className="border-2 border-slate-200 shadow-industrial">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Recent Damage Reports
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {damageReports.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No damage reports
            </div>
          ) : (
            <table className="table-industrial">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Description</th>
                  <th>Reported By</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {damageReports.slice(0, 10).map((report) => (
                  <tr key={report.id}>
                    <td>
                      <div>
                        <p className="font-body font-semibold">{report.asset_name}</p>
                        <p className="font-mono text-xs text-slate-500">{report.asset_id}</p>
                      </div>
                    </td>
                    <td className="max-w-xs truncate">{report.description}</td>
                    <td>{report.reported_by}</td>
                    <td className="font-mono text-sm">
                      {new Date(report.reported_at).toLocaleDateString()}
                    </td>
                    <td>
                      <span className={`badge-${report.status === 'open' ? 'error' : 'success'}`}>
                        {report.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
